import {useEffect, useState} from 'react';
import {useMutation} from '@apollo/client';
import {Button, DatePicker, Input, Select, Typography} from 'antd';
import moment from 'moment';
import {toast} from 'react-toastify';
import DBModal from 'components/modal/modal';
import {PRE_SIGN_MEDIA_URL} from 'graphql/mutations/wallet';
import {
  PROVENANCE_LABELS,
  needsBeneficialOwner,
  uploadHubFile,
} from './kycHubUtils';
import {BeneficialOwnerSelect} from './BeneficialOwnerSelect';
import styles from './kycHub.module.scss';

const {Text} = Typography;

const EMPTY = {
  documentType: undefined,
  freeName: '',
  beneficialOwnerId: undefined,
  provenance: 'CUSTOMER',
  providedAt: null,
  file: null,
};

/**
 * Upload a file, then register it as a new document — or, with `replacing`,
 * as the next version of that document.
 *
 * The file goes to S3 first; `onRegister` is only called once the PUT has
 * succeeded, so a failed upload never leaves a row pointing at nothing.
 * `onRegister` resolves true when the document was registered.
 *
 * On a business, a beneficial owner's ID names the owner it belongs to —
 * `beneficialOwners` are the account's declared owners to pick from.
 */
export const UploadDocumentModal = ({
  open,
  replacing,
  subject,
  documentTypes,
  beneficialOwners,
  onClose,
  onRegister,
}) => {
  const [form, setForm] = useState(EMPTY);
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [presign] = useMutation(PRE_SIGN_MEDIA_URL);

  useEffect(() => {
    if (open) {
      setForm(EMPTY);
      setTouched(false);
    }
  }, [open]);

  const set = patch => setForm(current => ({...current, ...patch}));
  const ownerRequired =
    !replacing && needsBeneficialOwner(subject?.subjectType, form.documentType);

  const errors = {
    documentType: !replacing && !form.documentType && 'Pick a document type.',
    freeName:
      !replacing &&
      form.documentType === 'OTHER' &&
      !form.freeName.trim() &&
      'Name this document.',
    beneficialOwnerId:
      ownerRequired &&
      !form.beneficialOwnerId &&
      'Pick the beneficial owner this ID belongs to.',
    file: !form.file && 'Choose a file.',
  };
  const invalid = Object.values(errors).some(Boolean);

  const handleSubmit = async () => {
    setTouched(true);
    if (invalid) return;

    setBusy(true);
    try {
      const uploaded = await uploadHubFile({
        presign,
        file: form.file,
        ...subject,
      });
      if (uploaded.error) {
        toast.error(uploaded.error);
        return;
      }
      const registered = await onRegister({
        fileUrl: uploaded.fileUrl,
        fileName: uploaded.fileName,
        ...(!replacing && {
          documentType: form.documentType,
          ...(form.documentType === 'OTHER' && {
            freeName: form.freeName.trim(),
          }),
          ...(ownerRequired && {beneficialOwnerId: form.beneficialOwnerId}),
          provenance: form.provenance,
          // Noon UTC, so the calendar day survives every timezone.
          ...(form.providedAt && {
            providedAt: `${form.providedAt.format('YYYY-MM-DD')}T12:00:00.000Z`,
          }),
        }),
      });
      if (registered) onClose();
    } catch (error) {
      toast.error('Could not upload this file. Try again.');
    } finally {
      setBusy(false);
    }
  };

  const fieldError = key =>
    touched &&
    errors[key] && (
      <Text type="danger" className={styles.fieldError}>
        {errors[key]}
      </Text>
    );

  return (
    <DBModal
      isOpen={open}
      handleClose={busy ? undefined : onClose}
      width={520}
      destroyOnClose
      content={
        <div className={styles.modalBody}>
          <h2 className={styles.modalTitle}>
            {replacing ? 'Replace document' : 'Add document'}
          </h2>
          {replacing && (
            <Text type="secondary">
              {replacing.freeName || replacing.fileName}. The new file becomes a
              new version pending review; this one moves to the history.
            </Text>
          )}

          {!replacing && (
            <>
              <div className={styles.modalField}>
                <label className={styles.fieldLabel}>Document type</label>
                <Select
                  className={styles.fullWidth}
                  placeholder="Choose a type"
                  value={form.documentType}
                  onChange={documentType => set({documentType})}
                  optionLabelProp="label">
                  {documentTypes.map(type => (
                    <Select.Option
                      key={type.code}
                      value={type.code}
                      label={type.en}>
                      <div className={styles.cell}>
                        <span>{type.en}</span>
                        <span className={styles.subtle}>French: {type.fr}</span>
                      </div>
                    </Select.Option>
                  ))}
                </Select>
                {fieldError('documentType')}
              </div>

              {form.documentType === 'OTHER' && (
                <div className={styles.modalField}>
                  <label className={styles.fieldLabel}>Document name</label>
                  <Input
                    value={form.freeName}
                    maxLength={120}
                    placeholder="e.g. Inheritance declaration"
                    onChange={event => set({freeName: event.target.value})}
                  />
                  {fieldError('freeName')}
                </div>
              )}

              {ownerRequired && (
                <div className={styles.modalField}>
                  <label
                    className={styles.fieldLabel}
                    htmlFor="kyc-hub-upload-owner">
                    Beneficial owner
                  </label>
                  <BeneficialOwnerSelect
                    id="kyc-hub-upload-owner"
                    owners={beneficialOwners}
                    value={form.beneficialOwnerId}
                    onChange={beneficialOwnerId => set({beneficialOwnerId})}
                  />
                  {fieldError('beneficialOwnerId')}
                </div>
              )}

              <div className={styles.modalField}>
                <label className={styles.fieldLabel}>Supplied by</label>
                <Select
                  className={styles.fullWidth}
                  value={form.provenance}
                  onChange={provenance => set({provenance})}
                  options={Object.entries(PROVENANCE_LABELS).map(
                    ([value, label]) => ({value, label}),
                  )}
                />
                <Text type="secondary" className={styles.fieldHint}>
                  Who the file came from. You are recorded as the uploader
                  either way.
                </Text>
              </div>

              <div className={styles.modalField}>
                <label className={styles.fieldLabel}>
                  Date provided (optional)
                </label>
                <DatePicker
                  className={styles.fullWidth}
                  value={form.providedAt}
                  disabledDate={date => date && date.isAfter(moment(), 'day')}
                  onChange={providedAt => set({providedAt})}
                />
              </div>
            </>
          )}

          <div className={styles.modalField}>
            <label className={styles.fieldLabel} htmlFor="kyc-hub-file">
              File
            </label>
            <input
              id="kyc-hub-file"
              type="file"
              disabled={busy}
              onChange={event => set({file: event.target.files?.[0] || null})}
            />
            {fieldError('file')}
          </div>

          <div className={styles.modalActions}>
            <Button onClick={onClose} disabled={busy}>
              Cancel
            </Button>
            {/* Disabled for the whole upload and registration, so a second
                click cannot store the file twice. */}
            <Button
              type="primary"
              loading={busy}
              disabled={busy}
              onClick={handleSubmit}>
              {busy
                ? 'Uploading…'
                : replacing
                ? 'Upload new version'
                : 'Upload document'}
            </Button>
          </div>
        </div>
      }
    />
  );
};
